/* eslint-disable max-lines */
import type {ReadFileOutput} from '@gaia/hooks/useReadFileEffect';
import waypointIcons from '@gaia/icons/waypoints/icons';
import pinIcons from '@gaia/icons/waypoints/pins';
import fetchJSON from '@gaia/utils/fetchJSON';
import isPolygonValid from '@gaia/utils/gaia/isPolygonValid';
import {getTrackStatsCalculator} from '@gaia/utils/gaia/TrackStatsCalculator';
import hasValidCoordinates from '@gaia/utils/hasValidCoordinates';
import type {
    Feature,
    FeatureCollection,
    Geometry,
    LineString,
    Point,
    Polygon,
    Position,
} from 'geojson';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';

import loadFit from './loadFit';

export type ParseOutput = [string | undefined, FeatureCollection | undefined];

type SinglePartGeometry = Point | Polygon | LineString;

type NormalizedFeature = Feature<
    SinglePartGeometry,
    (Partial<Waypoint['properties']> | Partial<RemoteTrack['properties']>) & {
        isValid: boolean;
        filename: string;
    }
>;

/**
 * Extract the notes from a feature from one of several possibilities on the
 * `feature.properties` object.
 *
 * Version 5 of the `@tmcw/togeojson` package parses some GPX/KML CDATA fields
 * into an object like `{ '@type': 'html', value: string}` instead of a string.
 */
function getNotes(feature: Feature<SinglePartGeometry>) {
    // type implemented in @tmcw/togeojson v5; so far `'html'` is the only
    // value used for `@type`
    type FeatureNotes =
        | string
        | {
              '@type': 'html';
              value: string;
          };

    const notes: FeatureNotes | undefined =
        feature.properties?.notes ??
        feature.properties?.comments ??
        // cmt is the GPX spec field for notes
        feature.properties?.cmt ??
        // Our GPX export uses desc
        feature.properties?.desc ??
        // Our KML export uses description
        feature.properties?.description;

    if (notes === undefined) return undefined;

    if (typeof notes === 'string') return notes;

    if (typeof notes.value === 'string') {
        return notes.value;
    }

    console.error(`Unexpected notes type:`, notes);

    // fall-through return would be equivalent, but let's be explicit about it
    return undefined;
}

function getColor(feature: Feature<SinglePartGeometry>) {
    let stroke = feature.properties?.stroke;
    // The topografix spec does not include the #
    // see http://www.topografix.com/GPX/gpx_style/0/2/#type_colorType
    if (stroke && stroke[0] !== '#') {
        stroke = `#${stroke}`;
    }

    // gpxx_RouteExtension is used by Garmin
    const color = feature.properties?.color || stroke || feature.properties?.gpxx_RouteExtension;

    if (typeof color !== 'string') return undefined;

    if (/^#[0-9A-F]{6}$/i.test(color)) {
        return color;
    }

    return undefined;
}

function getTimeCreated(feature: Feature<SinglePartGeometry>) {
    const timeCreated =
        feature.properties?.time_created ??
        feature.properties?.timeCreated ??
        feature.properties?.time;

    if (timeCreated) {
        try {
            return new Date(timeCreated).toISOString();
        } catch (e) {
            // no-op
        }
    }

    return undefined;
}

export function normalizeGeometry(geometry: SinglePartGeometry): SinglePartGeometry {
    // Waypoints only have an x and a y
    if (geometry.type === 'Point') {
        return {
            type: 'Point',
            coordinates: geometry.coordinates.splice(0, 2),
        };
        // Tracks and Routes have x, y, z, and m
    } else if (geometry.type === 'LineString') {
        return {
            type: 'LineString',
            coordinates: geometry.coordinates.map((coord) => {
                return [coord[0], coord[1], coord[2] || 0, coord[3] || 0];
            }),
        };
        // Areas have x, y, and z
    } else if (geometry.type === 'Polygon') {
        return {
            type: 'Polygon',
            coordinates: geometry.coordinates.map((ring) => {
                return ring.map((coord) => {
                    return [coord[0], coord[1], coord[2] || 0];
                });
            }),
        };
    }
    throw new Error('Geometry type not supported');
}

const allIcons = {...waypointIcons, ...pinIcons};
const icons: [RegExp, string][] = Object.keys(allIcons).map((icon) => {
    const iconString = icon.replace('-24', '').split('-').join(' ');
    return [new RegExp(`${iconString}\\b`, 'g'), icon];
});

export function getIcon(feature: Feature<SinglePartGeometry>) {
    // sym is used by GPX
    let icon: string | undefined = feature.properties?.icon || feature.properties?.sym;

    // Handle old icons
    if (icon) icon = icon.replace('.png', '');

    // If the feature has an emoji icon, use it. It is assumed to be valid.
    if (icon && icon.startsWith('emoji-')) return icon;

    // If the feature has one of our icons, use it
    if (feature.geometry.type === 'Point' && icon && icon in allIcons) {
        return icon;
    }

    const photoRegex = new RegExp('photo\\b', 'g');

    // If the title or notes contain a string similar to one of our icons, use it
    const title: string | undefined = feature.properties?.title;

    if (title) {
        const lowerCaseTitle = title.toLowerCase();
        if (photoRegex.test(lowerCaseTitle)) return 'camera-24.png';
        for (let i = 0; i < icons.length; i++) {
            if (icons[i][0].test(title)) return icons[i][1];
        }
    }

    const notes = getNotes(feature);

    if (notes) {
        const lowerCaseNotes = notes.toLowerCase();
        if (photoRegex.test(lowerCaseNotes)) return 'camera-24.png';
        for (let i = 0; i < icons.length; i++) {
            if (icons[i][0].test(lowerCaseNotes)) return icons[i][1];
        }
    }

    return;
}

function getDistance(
    feature: Feature<SinglePartGeometry>,
    calculator: TrackStatsCalculator
): number | undefined {
    if (feature.geometry.type !== 'LineString') return undefined;
    calculator.addGeoJson(feature);
    calculator.filter();
    return calculator.getJsonStats().distance;
}

export function normalizeFeature(
    feature: Feature<SinglePartGeometry>,
    filename: string,
    markerDetails: MarkerDetails | undefined,
    calculator: TrackStatsCalculator
): NormalizedFeature {
    const trackType: any = {};
    if (feature.geometry.type === 'Polygon') {
        trackType['track_type'] = 'polygon';
    } else if (feature.geometry.type === 'LineString') {
        if (feature.properties?._gpxType === 'rte' || feature.properties?.track_type === 'route') {
            trackType['track_type'] = 'route';
        } else {
            // Tracks have an undefined/null `track_type`
            trackType['track_type'] = undefined;
        }
    }

    const color = getColor(feature);
    const timeCreated = getTimeCreated(feature);
    const distance = getDistance(feature, calculator);
    const title = feature.properties?.title ?? feature.properties?.name;
    const notes = getNotes(feature);

    return {
        type: 'Feature',
        geometry: normalizeGeometry(feature.geometry),
        properties: {
            ...(title && {title}),
            ...(notes && {notes}),
            ...(timeCreated && {time_created: timeCreated}),
            ...(color && {color}),
            ...(markerDetails && markerDetails),
            ...(distance && {distance}),
            ...('track_type' in trackType && {routing_mode: null}),
            ...trackType,
            isValid: feature.properties?.isValid,
            isLatestImport: true,
            filename,
        },
    };
}

export function flattenGeometry(geometry: Geometry): SinglePartGeometry[] {
    // sanity typecheck
    if (!geometry || !geometry.type) return [];

    if (geometry.type === 'GeometryCollection') {
        const geometries = flatten(geometry.geometries.map((geom) => flattenGeometry(geom)));
        return geometries;
    }

    switch (geometry.type) {
        case 'MultiPoint': {
            const geometries = geometry.coordinates.map(
                (coordinates): Point => ({
                    type: 'Point',
                    coordinates,
                })
            );
            return geometries;
        }
        case 'MultiLineString': {
            const geometries = geometry.coordinates.map(
                (coordinates): LineString => ({
                    type: 'LineString',
                    coordinates,
                })
            );
            return geometries;
        }
        case 'MultiPolygon': {
            const geometries = geometry.coordinates.map(
                (coordinates): Polygon => ({
                    type: 'Polygon',
                    coordinates,
                })
            );
            return geometries;
        }
    }

    return [geometry];
}

function validateFeature(feature: Feature<SinglePartGeometry>): Feature<SinglePartGeometry> {
    let isValid = true;
    if (
        !hasValidCoordinates(feature.geometry) ||
        (feature.geometry.type === 'Polygon' && !isPolygonValid(feature.geometry))
    ) {
        isValid = false;
    }

    return {
        ...feature,
        properties: {
            ...feature.properties,
            isValid,
        },
    };
}

function loadJSON(fileContents: string): ParseOutput {
    let geojson;
    try {
        geojson = JSON.parse(fileContents);
    } catch (e) {
        console.error(e);
        return ['The file is not valid JSON', undefined];
    }
    if (!geojson.type || !geojson.features || geojson.type !== 'FeatureCollection') {
        return ['The GeoJSON must be a FeatureCollection and contain features', undefined];
    }
    return [undefined, geojson];
}

/**
 * N.b. we ensure every coordinate tuple has 4 dimensions;
 * If elev is present, then it's `[number, number, number, number]`
 * If not, then `[number, number, undefined, number]`
 * Finally, if we can't parse time, then we use `undefined`
 *
 * So this returns:
 * `[number, number, number?, number?]`
 */
function toCoord4D(coordinate: Position, timeIso: string | undefined) {
    const [lng, lat, elev] = coordinate;

    let time: undefined | number;

    try {
        time = new Date(timeIso as string).getTime() / 1000;
    } catch {
        time = NaN;
    } finally {
        if (typeof time !== 'number' || Number.isNaN(time) || !isFinite(time)) time = undefined;
    }

    return [lng, lat, elev, time] as Position;
}

function loadXML(fileContents: FeatureCollection): ParseOutput {
    // The GPX parser puts time stamps in the properties but we expect them in the geometry
    return [
        undefined,
        {
            type: 'FeatureCollection',
            features: fileContents.features
                // `geometry` may be `null` ([https://www.rfc-editor.org/rfc/rfc7946#section-3.2])
                .filter((feature) => feature.geometry !== null)
                .map((feature) => {
                    const {geometry} = feature;

                    if (geometry.type !== 'LineString' && geometry.type !== 'MultiLineString') {
                        return feature;
                    }

                    const coordTimes = feature.properties?.coordinateProperties?.times;

                    if (!Array.isArray(coordTimes)) {
                        return feature;
                    }

                    // We assume (maybe dangerously) that
                    // `feature.properties.coordinateProperties` has the correct
                    // format and length according to Mapbox's spec, and that
                    // `@tmcw/togeojson` has parsed it correctly.
                    // [https://github.com/mapbox/geojson-coordinate-properties]

                    switch (geometry.type) {
                        case 'LineString': {
                            return {
                                ...feature,
                                geometry: {
                                    ...geometry,
                                    coordinates: geometry.coordinates.map((coord, i) =>
                                        toCoord4D(coord, coordTimes[i])
                                    ),
                                },
                            };
                        }
                        case 'MultiLineString': {
                            return {
                                ...feature,
                                geometry: {
                                    ...geometry,
                                    coordinates: geometry.coordinates.map(
                                        (segment, segmentIndex) => {
                                            return segment.map((coord, i) =>
                                                toCoord4D(coord, coordTimes[segmentIndex][i])
                                            );
                                        }
                                    ),
                                },
                            };
                        }
                    }
                }),
        },
    ];
}

async function loadFile({fileContents, type}: ReadFileOutput): Promise<ParseOutput> {
    // Typescript isn't able to properly infer types here so we have to coerce the type :-(
    if (type === 'geojson') {
        return loadJSON(fileContents as string);
    } else if (type === 'gpx' || type === 'kml' || type === 'kmz') {
        return loadXML(fileContents as FeatureCollection);
    } else if (type === 'fit') {
        return loadFit(fileContents as ArrayBuffer);
    } else {
        // no-op
        return new Promise((resolve) => resolve(['Unknown file type', undefined]));
    }
}

// Accepts a file of type VALID_FILE_TYPES and returns a GeoJSON FeatureCollection
export default async function parseFile(
    file: ReadFileOutput,
    origin: string
): Promise<ParseOutput> {
    const [error, geojson] = await loadFile(file);
    if (error || !geojson) return [error, undefined];

    const flatFeatures: Feature<SinglePartGeometry>[] = flatten(
        geojson.features.map((feature) => {
            return flattenGeometry(feature.geometry).map((geometry) => ({
                type: 'Feature',
                properties: feature.properties,
                geometry,
            }));
        })
    );

    const validFeatures = flatFeatures
        .map(validateFeature) // sets `properties.isValid: true`
        .filter((feature) => feature.properties?.isValid === true);

    const featureIcons = compact(validFeatures.map((feature) => getIcon(feature)));

    const iconToMarkerMap: Record<string, MarkerDetails> =
        featureIcons.length > 0
            ? await fetchJSON(`${origin}/api/objects/waypoint/icon_to_marker/`, {
                  method: 'POST',
                  body: JSON.stringify({
                      icons: featureIcons,
                      is_legacy_client: false,
                  }),
                  headers: {
                      'Content-Type': 'application/json',
                  },
              })
            : {};

    const calculator = await getTrackStatsCalculator();

    const normalizedValidFeatures = validFeatures.map((feature) =>
        normalizeFeature(
            feature,
            file.filename,
            iconToMarkerMap[getIcon(feature) ?? ''],
            calculator
        )
    );

    if (!normalizedValidFeatures.length) {
        return [`No valid features were found in ${file.filename}`, undefined];
    }

    return [undefined, {type: 'FeatureCollection', features: normalizedValidFeatures}];
}

type MarkerDetails = {
    marker_type: MarkerType;
    marker_decoration: string | undefined;
    marker_color: GaiaColorHex;
};

export type ParseFile = {
    parseFile: typeof parseFile;
};
